import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

import { PageHeader, btn, Loader } from "@/components/Page";
import { useMiningDashboard } from "@/hooks/useMiningDashboard";
import { MiningTabs } from "@/components/mining/MiningTabs";
import { MiningSummaryPanel } from "@/components/mining/MiningSummaryPanel";
import { MiningSessionPanel } from "@/components/mining/MiningSessionPanel";
import { MiningAutoConfigPanel } from "@/components/mining/MiningAutoConfigPanel";
import { MineLocationCard } from "@/components/mining/MineLocationCard";
import { MiningToolPanel } from "@/components/mining/MiningToolPanel";
import { MiningResourcesPanel } from "@/components/mining/MiningResourcesPanel";
import { MiningConfirmDialog } from "@/components/mining/MiningConfirmDialog";
import { MiningEquipmentLink } from "@/components/mining/MiningEquipmentLink";

function OreRushBanner({ event }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(timer); }, []);
  if (!event?.active) return null;
  const remaining = Math.max(0, new Date(event.active_until).getTime() - now);
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  return <section data-testid="ore-rush-mining-banner" className="border border-amber-500/60 bg-amber-950/30 px-5 py-4"><div className="text-xs uppercase tracking-[0.2em] text-amber-300">Ore Rush Event</div><div className="mt-1 font-cinzel text-xl text-amber-100">3× discovery chance · 2× raw ore</div><p data-testid="ore-rush-mining-copy" className="mt-1 text-sm text-zinc-300">Active for online expeditions and offline auto-mining · {days}d {hours}h remaining.</p></section>;
}

const MINING_BANNER_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_56f1548c-1634-41b4-91f8-8c60c0e06355/artifacts/gxf5cprw_Mining.jpg";

export default function Mining() {
  const [confirm, setConfirm] = useState(null);
  const {
    data,
    loading,
    loadMining,
    liveSession,
    busyMineId,
    busyToolId,
    claimBusy,
    handleStart,
    handleClaim,
    handlePurchaseTool,
    handleEquipTool,
    handleRepairTool,
    autoMineSaving,
    handleSaveAutoMine,
    liveSmithing,
  } = useMiningDashboard();

  const confirmBusy = (confirm?.type === "start" && busyMineId === confirm?.mineId) || (confirm?.type === "tool" && busyToolId === confirm?.toolId);
  const hasBlockingMiningState = !!data?.pending_claim || (!!data?.current_session && (liveSession?.time_remaining_seconds ?? 0) > 0);
  const brokenPickaxeReason = data?.equipped_tool && !(data?.equipped_tool?.usable ?? false)
    ? `${data.equipped_tool.name} is broken and must be repaired before you can mine.`
    : "";
  const highestUnlockedMineId = [...(data?.mines || [])]
    .filter((mine) => mine.unlocked)
    .sort((left, right) => (right.required_level || 0) - (left.required_level || 0))[0]?.id || null;
  const mineJumpLinks = (data?.mines || []).map((mine) => ({
    targetId: `mining-mine-anchor-${mine.id}`,
    testId: mine.id,
    label: mine.name,
  }));

  if (loading && !data) return <Loader label="Loading Mining..." />;

  return (
    <div data-testid="mining-page" className="space-y-6">
      <PageHeader
        title="Mining"
        subtitle="Run expeditions, manage tools, and gather raw ore for later refinement."
        action={
          <button data-testid="mining-refresh-button" type="button" onClick={() => loadMining()} className={btn("ghost") + " flex items-center gap-2"}>
            <RefreshCw size={15} /> Refresh
          </button>
        }
      />

      <MiningEquipmentLink />

      <OreRushBanner event={data?.ore_rush_event} />

      <MiningTabs claimableCount={data?.discoveries?.claimable_count || 0} />

      <div className="relative overflow-hidden border border-amber-900/40 rounded-sm" data-testid="mining-banner-shell">
        <img
          src={MINING_BANNER_URL}
          alt="A miner carving ore from a torchlit cavern"
          data-testid="mining-banner"
          className="w-full h-44 sm:h-60 object-cover object-[center_28%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
        <h2 className="absolute bottom-3 left-5 font-cinzel text-2xl sm:text-3xl text-amber-200 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
          The Mines
        </h2>
      </div>

      <div className="grid xl:grid-cols-[1.15fr_0.85fr] gap-6">
        <MiningSummaryPanel profile={data?.profile} smithing={liveSmithing || data?.smithing} jumpLinks={mineJumpLinks} />
        <MiningSessionPanel currentSession={liveSession} pendingClaim={data?.pending_claim} claimBusy={claimBusy} onClaim={handleClaim} />
      </div>

      <MiningAutoConfigPanel
        autoMine={data?.offline_auto_mine}
        report={data?.offline_auto_report}
        mines={data?.mines || []}
        saving={autoMineSaving}
        onSave={handleSaveAutoMine}
      />

      <MiningResourcesPanel profile={data?.profile} recentResults={data?.recent_results} />
      <section data-testid="mining-locations-section" className="space-y-4">
        <div>
          <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-wide">Mine Selection</h2>
          <p className="text-xs text-zinc-500 mt-1">Higher mining levels unlock deeper locations. Risk simply means how demanding the run is: higher risk uses more energy, wears down your pickaxe faster, and usually offers better rewards.</p>
        </div>
        <div className="grid xl:grid-cols-2 gap-4">
          {(data?.mines || []).map((mine) => (
            <MineLocationCard
              key={mine.id}
              anchorId={`mining-mine-anchor-${mine.id}`}
              mine={mine}
              isHighestUnlocked={mine.id === highestUnlockedMineId}
              canStart={!hasBlockingMiningState && (data?.equipped_tool?.usable ?? false)}
              startBlockedReason={!hasBlockingMiningState ? brokenPickaxeReason : ""}
              busy={busyMineId === mine.id}
              onStart={(mineId) => {
                const selectedMine = (data?.mines || []).find((entry) => entry.id === mineId);
                if (!selectedMine) return;
                setConfirm({
                  type: "start",
                  mineId,
                  title: "Confirm Mining Session",
                  message: `Start Mining at ${selectedMine.name}?\n\nThis run will use ${selectedMine.energy_cost} energy and take ${selectedMine.duration_label}.`,
                  confirmLabel: "Start Mining",
                  onConfirm: async () => {
                    await handleStart(mineId);
                    setConfirm(null);
                  },
                });
              }}
            />
          ))}
        </div>
      </section>
      <MiningToolPanel
        equippedTool={data?.equipped_tool}
        tools={data?.tools || []}
        busyToolId={busyToolId}
        onPurchase={(toolId) => {
          const tool = (data?.tools || []).find((entry) => entry.id === toolId);
          if (!tool) return;
          setConfirm({
            type: "tool",
            toolId,
            title: "Confirm Pickaxe Purchase",
            message: `Buy ${tool.name} for ${tool.cost_gold.toLocaleString()} gold?\n\nThis will immediately add it to your mining tools.`,
            confirmLabel: "Buy Pickaxe",
            onConfirm: async () => {
              await handlePurchaseTool(toolId);
              setConfirm(null);
            },
          });
        }}
        onEquip={handleEquipTool}
        onRepair={(toolId, currency = "gold") => {
          const tool = (data?.tools || []).find((entry) => entry.id === toolId);
          if (!tool) return;
          const follis = currency === "follis";
          const cost = follis ? tool.repair_follis_cost_current : tool.repair_cost_current;
          setConfirm({
            type: "tool",
            toolId,
            title: "Confirm Pickaxe Repair",
            message: `Repair ${tool.name} for ${cost.toLocaleString()} ${follis ? "follis" : "gold"}?\n\nThis restores it to full durability for mining.`,
            confirmLabel: "Repair Pickaxe",
            onConfirm: async () => {
              await handleRepairTool(toolId, currency);
              setConfirm(null);
            },
          });
        }}
      />

      <MiningConfirmDialog confirm={confirm} busy={confirmBusy} onClose={() => setConfirm(null)} />
    </div>
  );
}